import {
  GENCOW_APP_WAKE_CHANNEL,
  buildWakeNotifySql,
  normalizeWakeSignalChannel,
  parseWakeSignalPayload,
  type ParsedWakeSignal,
  type WakeSignalKind,
} from "./wake-signal-types";
import {
  createWakeSignalDiagnostics,
  type WakeSignalDiagnosticsSnapshot,
} from "./wake-signal-diagnostics";

type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;
type LoggerLike = Pick<Console, "warn">;
type WakeSignalUnsubscribe = () => void | Promise<void>;
type PostgresListenMeta = {
  unlisten(): Promise<void>;
};
type PostgresListenResult =
  | WakeSignalUnsubscribe
  | PostgresListenMeta
  | Promise<WakeSignalUnsubscribe | PostgresListenMeta>;

export type WakeSignalListenerSource = {
  listen(
    channel: string,
    handler: (payload: string) => void | Promise<void>,
    onListen?: () => void,
  ): WakeSignalUnsubscribe | Promise<WakeSignalUnsubscribe>;
};

export type PostgresWakeSignalListenClient = {
  listen(
    channel: string,
    handler: (payload: string) => void,
    onListen?: () => void,
  ): PostgresListenResult;
};

export type WakeSignalBus = {
  notify(kind: WakeSignalKind): Promise<void>;
  listen(
    kinds: readonly WakeSignalKind[],
    handler: (signal: ParsedWakeSignal) => void | Promise<void>,
    options?: WakeSignalListenOptions,
  ): Promise<() => Promise<void>>;
  getDiagnostics(): Promise<WakeSignalDiagnosticsSnapshot>;
};

export type WakeSignalListenOptions = {
  onListen?: () => void | Promise<void>;
};

export function createPostgresWakeSignalListenerSource(
  client: PostgresWakeSignalListenClient,
): WakeSignalListenerSource {
  return {
    async listen(channel, handler, onListen) {
      const unsubscribe = await client.listen(channel, (payload) => {
        void handler(payload);
      }, onListen);
      if (typeof unsubscribe === "function") return unsubscribe;
      return () => unsubscribe.unlisten();
    },
  };
}

export function createPostgresWakeSignalBus(params: {
  rawSql: RawSql;
  source?: WakeSignalListenerSource;
  channel?: string;
  defaultChannel?: string;
  logger?: LoggerLike;
}): WakeSignalBus {
  const defaultChannel = params.defaultChannel ?? GENCOW_APP_WAKE_CHANNEL;
  const channel = normalizeWakeSignalChannel(params.channel, defaultChannel);
  const diagnostics = createWakeSignalDiagnostics({
    channel,
    rawSql: params.rawSql,
  });
  let activeListenRegistrations = 0;
  let channelListenReady = false;

  return {
    async notify(kind) {
      const statement = buildWakeNotifySql({ kind, channel, defaultChannel });
      await params.rawSql(statement.query, statement.params);
    },

    async listen(kinds, handler, options) {
      if (!params.source) {
        throw new Error("PostgreSQL wake signal listener source is required");
      }

      const allowedKinds = new Set<WakeSignalKind>(kinds);
      diagnostics.setAllowedKinds(kinds);
      if (!channelListenReady && activeListenRegistrations === 0) diagnostics.markConnecting();
      activeListenRegistrations += 1;
      let listenReady = false;
      let initialListenReady: Promise<void> | null = null;

      const runListenReadyCallback = async () => {
        try {
          await options?.onListen?.();
        } catch (error) {
          diagnostics.recordHandlerFailure(error);
          const message = error instanceof Error ? error.message : String(error);
          params.logger?.warn(`[wake-signal] listen-ready callback failed: ${message.slice(0, 160)}`);
        }
      };

      const markListenReady = () => {
        const wasListenReady = listenReady;
        listenReady = true;
        if (!channelListenReady || wasListenReady) {
          diagnostics.markListening();
          channelListenReady = true;
        }
        const callbackRun = runListenReadyCallback();
        initialListenReady ??= callbackRun;
      };

      let unsubscribe: WakeSignalUnsubscribe;
      try {
        unsubscribe = await params.source.listen(
          channel,
          async (payloadText) => {
            const signal = parseWakeSignalPayload(payloadText);
            diagnostics.recordReceived(signal.kind, signal.malformed);
            if (signal.kind !== "unknown" && !allowedKinds.has(signal.kind)) {
              diagnostics.recordFiltered();
              return;
            }
            try {
              await handler(signal);
            } catch (error) {
              diagnostics.recordHandlerFailure(error);
              const message = error instanceof Error ? error.message : String(error);
              params.logger?.warn(`[wake-signal] handler failed: ${message.slice(0, 160)}`);
            }
          },
          markListenReady,
        );
      } catch (error) {
        activeListenRegistrations = Math.max(0, activeListenRegistrations - 1);
        if (activeListenRegistrations === 0) channelListenReady = false;
        diagnostics.markError(error);
        throw error;
      }
      if (!listenReady) markListenReady();
      await initialListenReady;

      return async () => {
        await unsubscribe();
        activeListenRegistrations = Math.max(0, activeListenRegistrations - 1);
        if (activeListenRegistrations === 0) {
          channelListenReady = false;
          diagnostics.markClosed();
        }
      };
    },

    getDiagnostics() {
      return diagnostics.getSnapshot();
    },
  };
}
